'use client';

import React, { useMemo, useEffect, useState, useRef } from 'react';
import { ClusteredProviderMarkers } from 'src/components/Maps/ClusteredProviderMarkers';
import { useSelector } from 'react-redux';
import {
  selectDiscoveryMapZoom,
  selectDiscoveryMapBounds,
  selectDiscoveryMapCurrentTerritory,
  selectDiscoveryMapMapBounds,
  selectAdvancedSearchRadius,
} from 'src/redux/slices/discoveryMapSlice';
import { useDebounce } from 'use-debounce';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  isReadyCohortVolume,
  resolveClaimsMetric,
} from 'src/utils/encounterLabels';
import { useGetDiscoveryMapQuery } from 'src/api/redux';
import {
  useGetAdvancedSearchMapQuery,
  useGetAdvancedSearchMetricsQuery,
} from 'src/api/redux/advancedSearches';
import { useDiscoveryMapQueryParams } from '../ResultsPanel/hooks/useDiscoveryQueryParams';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import useDiscoveryList from 'src/hooks/discovery/useDiscoveryList';
import { LOCATION } from 'src/hooks/filters/constants';
import useIsAdvancedSearchActive from 'src/hooks/discovery/useIsAdvancedSearchActive';
import { MapLoadingIndicator } from '../MapLoadingIndicator';
import buildAdvancedSearchMapParams from '../../utils/buildAdvancedSearchMapParams';
import extractClaimsPatients from '../../utils/extractClaimsPatients';
import { getMetricRows } from 'src/components/Maps/ProviderMarkers/getProviderPopupMetrics';

const MedMapMarkers = ({
  activeMarker,
  setActiveMarker,
  addToList,
  showMarkers,
}) => {
  // Selectors and hooks for map state
  const zoom = useSelector(selectDiscoveryMapZoom);
  const bounds = useSelector(selectDiscoveryMapBounds);
  const currentTerritory = useSelector(selectDiscoveryMapCurrentTerritory);
  const mapBounds = useSelector(selectDiscoveryMapMapBounds);
  const advancedSearchRadius = useSelector(selectAdvancedSearchRadius);

  const { appliedSavedVolume } = useSavedVolume(LOCATION.DISCOVERY);
  const { sort } = useDiscoveryList();
  const isAdvancedSearchSelected = useIsAdvancedSearchActive();
  const { showEncountersTotals } = useFlags();
  const isReadyCohort = isReadyCohortVolume(appliedSavedVolume);

  // Standard discovery map query — skip when advanced search is selected
  const { queryParams, skip: skipDiscovery } = useDiscoveryMapQueryParams();
  const {
    data: discoveryResults,
    isLoading: isDiscoveryLoading,
    isFetching: isDiscoveryFetching,
  } = useGetDiscoveryMapQuery(queryParams, {
    skip: skipDiscovery || isAdvancedSearchSelected || !showMarkers,
  });

  // Advanced search map query
  const advSearchMapParams = useMemo(() => {
    if (!isAdvancedSearchSelected) return null;
    return buildAdvancedSearchMapParams({
      advancedSearchId: appliedSavedVolume.id,
      zoom,
      currentTerritoryId: currentTerritory?.id,
      mapBounds,
      advancedSearchRadius,
    });
  }, [
    isAdvancedSearchSelected,
    appliedSavedVolume,
    currentTerritory?.id,
    mapBounds,
    zoom,
    advancedSearchRadius,
  ]);

  const {
    data: advSearchMapData,
    isLoading: isAdvSearchLoading,
    isFetching: isAdvSearchFetching,
  } = useGetAdvancedSearchMapQuery(advSearchMapParams, {
    skip: !isAdvancedSearchSelected || !advSearchMapParams || !showMarkers,
  });

  // Metric headers, to turn each point's metrics into the list's Claims and Patients.
  const { data: advSearchMetrics } = useGetAdvancedSearchMetricsQuery(
    appliedSavedVolume?.id,
    { skip: !isAdvancedSearchSelected || !appliedSavedVolume?.id }
  );

  const isMapLoading = isAdvancedSearchSelected
    ? isAdvSearchLoading || isAdvSearchFetching
    : isDiscoveryLoading || isDiscoveryFetching;

  // State to manage transition and marker stability
  const [isVisible, setIsVisible] = useState(false);
  const shouldRenderRef = useRef(false);

  // Merge results from either discovery or advanced search map
  const activeResults = useMemo(() => {
    if (isAdvancedSearchSelected) {
      if (!advSearchMapData) return undefined;
      // Popups follow the metric the list is sorted by (the first metric when the sort
      // isn't one). The metric headers are needed to read a point's metrics, so until
      // they load the points carry no volume.
      const metricHeaders = advSearchMetrics?.results;
      const selectedMetric =
        metricHeaders?.find((metric) =>
          metric.display_options?.some((option) => option.code === sort)
        ) ?? metricHeaders?.[0];
      // Only a volume sort orders a cluster popup; any other sort (percent, currency)
      // falls back to the list's own claims.
      const pinMetricCode = selectedMetric?.display_options?.find(
        (option) => option.code === sort && option.type === 'volume'
      )?.code;
      return advSearchMapData.results.map((result) => {
        // Aggregated cells have no metrics, so they carry no volume.
        if (!metricHeaders || !result.metrics) {
          return {
            ...result,
            id: result.provider_id,
            content_type: result.type,
          };
        }
        const { claims } = extractClaimsPatients(result.metrics, metricHeaders);
        const selectedValues: Record<string, number> =
          result.metrics[String(selectedMetric?.id)] ?? {};
        const pinVolume = pinMetricCode ? selectedValues[pinMetricCode] : undefined;
        const metricRows = getMetricRows(
          selectedMetric?.display_options,
          selectedValues
        );
        return {
          ...result,
          id: result.provider_id,
          content_type: result.type,
          // `volume` orders the providers in a cluster popup; markers never show it.
          volume: typeof pinVolume === 'number' ? pinVolume : claims,
          volumeName: selectedMetric?.label ?? appliedSavedVolume?.label,
          metricRows,
        };
      });
    }
    if (skipDiscovery) return undefined;
    // The popups show the same volume as the results list (claims, or encounters for
    // a ready cohort behind the flag); markers never show it. Aggregated cells carry
    // no per-provider volume, so `volume` stays undefined and the popups omit it. The
    // map endpoint returns claims only, so there is no patients value to show here.
    // An aggregated cell's `count` is how many providers it holds, which is what its
    // marker and the clusters around it add up, so the map matches the list's total.
    return discoveryResults?.results?.map((result) => {
      const { value, label } = resolveClaimsMetric(
        result,
        isReadyCohort,
        !!showEncountersTotals
      );
      return {
        ...result,
        providerCount: typeof result.count === 'number' ? result.count : 1,
        volume: typeof value === 'number' ? value : undefined,
        volumeLabel: label,
        volumeName: appliedSavedVolume?.label,
      };
    });
  }, [
    isReadyCohort,
    appliedSavedVolume?.label,
    showEncountersTotals,
    isAdvancedSearchSelected,
    advSearchMapData,
    advSearchMetrics?.results,
    sort,
    skipDiscovery,
    discoveryResults,
  ]);
  const [debouncedSearchResults] = useDebounce(activeResults, 100);
  const [debouncedBounds] = useDebounce(bounds, 150);

  // Handle visibility transitions when showMarkers changes
  useEffect(() => {
    if (showMarkers) {
      shouldRenderRef.current = true;
      setTimeout(() => setIsVisible(true), 50);
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        shouldRenderRef.current = false;
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showMarkers]);

  // Use memoized bounds to prevent unnecessary recalculations
  const memoizedBounds = useMemo(() => {
    if (!debouncedBounds) return undefined;
    // Format as [west, south, east, north] which is what useSupercluster expects
    return [
      debouncedBounds[0][1], // west
      debouncedBounds[1][0], // south
      debouncedBounds[1][1], // east
      debouncedBounds[0][0], // north
    ] as [number, number, number, number]; // Type assertion to fix TS error
  }, [debouncedBounds]);

  // Show loading indicator - check before early returns
  if (isMapLoading) {
    return <MapLoadingIndicator message="Loading markers..." />;
  }

  if (
    !debouncedBounds ||
    (!debouncedSearchResults && !isMapLoading) ||
    !shouldRenderRef.current
  )
    return null;

  return (
    <div
      className={`
      relative z-10
      ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}
      transition-all duration-300 ease-out
      will-change-transform
    `}
    >
      <ClusteredProviderMarkers
        points={showMarkers ? debouncedSearchResults : []}
        bounds={memoizedBounds}
        zoom={zoom}
        activeMarker={activeMarker}
        setActiveMarker={setActiveMarker}
        addToList={addToList}
      />
    </div>
  );
};

export default MedMapMarkers;
